import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api.js";
import Shell from "./Shell.jsx";

const LINK_MINUTES = 15;

/**
 * Sign-in by emailed link (DEC-033). Without a token it asks for an address and sends a link;
 * on /signin#token=… it exchanges the token for a session. That step waits for a click instead of
 * running on load, because mail scanners open links to inspect them and would use the token up.
 */
export default function SignIn({ tenant, onSignedIn }) {
  const location = useLocation();
  const navigate = useNavigate();
  const brand = tenant.brand?.name || tenant.name;
  const [token] = useState(() => new URLSearchParams(location.hash.slice(1)).get("token"));
  const [state, setState] = useState(token ? "confirm" : "ask"); // ask | sending | sent | confirm | signing | invalid | error
  const [email, setEmail] = useState("");
  const [problem, setProblem] = useState("");

  // Once read, the token leaves the address bar and the history.
  useEffect(() => {
    if (location.hash) navigate(location.pathname, { replace: true });
  }, [location.hash, location.pathname, navigate]);

  if (tenant.user && !token) return <Navigate to="/" replace />;

  const send = async e => {
    e.preventDefault();
    setProblem("");
    setState("sending");
    try {
      await api.requestLink(email);
      setState("sent");
    } catch (err) {
      setState("ask");
      setProblem(
        err.code === "bad_email"
          ? "That doesn't look like an email address."
          : err.code === "too_many_requests"
            ? "Too many requests from this network. Try again in a few minutes."
            : "The link couldn't be sent. Try again."
      );
    }
  };

  const confirm = async () => {
    setState("signing");
    try {
      await api.signIn(token);
      await onSignedIn();
      navigate("/", { replace: true });
    } catch (err) {
      setState(err.code === "link_invalid" ? "invalid" : "error");
    }
  };

  const startOver = () => {
    setProblem("");
    setState("ask");
    if (location.pathname === "/signin") navigate("/", { replace: true });
  };

  return (
    <div className="app">
      <Shell tenant={tenant} />
      <main className="app-signin">
        <section className="app-signin-card" aria-live="polite">
          {(state === "ask" || state === "sending") && (
            <>
              <h1>Sign in to {brand}</h1>
              <p className="app-muted">We'll email you a link. It works once, for {LINK_MINUTES} minutes.</p>
              <form onSubmit={send}>
                <label htmlFor="signin-email">Email</label>
                <input
                  id="signin-email"
                  type="email"
                  autoComplete="email"
                  required
                  autoFocus
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
                <button className="app-primary" type="submit" disabled={state === "sending"}>
                  {state === "sending" ? "Sending…" : "Email me a sign-in link"}
                </button>
              </form>
              {problem && (
                <p className="app-signin-problem" role="alert">
                  {problem}
                </p>
              )}
            </>
          )}

          {state === "sent" && (
            <>
              <h1>Check your email</h1>
              <p>
                If <b>{email}</b> has access to {brand}, a sign-in link is on its way. It works once, for {LINK_MINUTES} minutes.
              </p>
              <button className="app-linkbtn" type="button" onClick={startOver}>
                Use a different address
              </button>
            </>
          )}

          {(state === "confirm" || state === "signing") && (
            <>
              <h1>Sign in to {brand}</h1>
              <p className="app-muted">Continue to finish signing in on this device.</p>
              <button className="app-primary" type="button" autoFocus disabled={state === "signing"} onClick={confirm}>
                {state === "signing" ? "Signing in…" : "Continue"}
              </button>
            </>
          )}

          {(state === "invalid" || state === "error") && (
            <>
              <h1>{state === "invalid" ? "This link no longer works" : "Something went wrong"}</h1>
              <p className="app-muted">
                {state === "invalid"
                  ? `Sign-in links work once, for ${LINK_MINUTES} minutes. Ask for a new one.`
                  : "Signing in didn't finish. Ask for a new link and try again."}
              </p>
              <button className="app-primary" type="button" onClick={startOver}>
                Send a new link
              </button>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
